import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Plus, FileText, Trash2, Languages, Briefcase, Sparkles, Copy } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { PageHeader } from "@/components/PageHeader";
import { useI18n } from "@/context/I18nContext";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";

function creditError(err, t) {
  if (err.response?.status === 402) { toast.error(err.response.data?.detail?.message || "Crédits insuffisants"); return true; }
  toast.error(err.response?.data?.detail || t("common.error")); return false;
}

function CVTab() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const [cvs, setCvs] = useState([]);
  const [title, setTitle] = useState("");
  const [open, setOpen] = useState(false);
  const load = () => api.get("/work/cvs").then(({ data }) => setCvs(data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const create = async () => {
    try {
      const { data } = await api.post("/work/cvs", { title: title || t("work.newCV"), data: {} });
      setOpen(false); setTitle(""); navigate(`/work/cv/${data.id}`);
    } catch (e) { toast.error(t("common.error")); }
  };
  const del = async (id) => { await api.delete(`/work/cvs/${id}`); load(); };
  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <h3 className="font-display font-semibold">{t("work.myCVs")}</h3>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button size="sm" className="rounded-full" data-testid="new-cv-btn"><Plus className="h-4 w-4 mr-1" />{t("work.newCV")}</Button></DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>{t("work.newCV")}</DialogTitle></DialogHeader>
            <Label>{t("work.cvTitle")}</Label>
            <Input value={title} onChange={(e) => setTitle(e.target.value)} data-testid="cv-title-input" placeholder="Ex: CV Développeur" />
            <DialogFooter><Button onClick={create} data-testid="create-cv-confirm">{t("common.create")}</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
      {cvs.length === 0 && <p className="text-sm text-muted-foreground py-8 text-center" data-testid="no-cvs">{t("work.noCVs")}</p>}
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {cvs.map((cv) => (
          <div key={cv.id} className="rounded-xl border bg-card p-4 flex items-start justify-between group" data-testid="cv-card">
            <button className="text-left flex-1" onClick={() => navigate(`/work/cv/${cv.id}`)}>
              <FileText className="h-5 w-5 text-primary mb-2" />
              <div className="font-medium">{cv.title}</div>
            </button>
            <button onClick={() => del(cv.id)} className="text-muted-foreground hover:text-destructive" data-testid="delete-cv"><Trash2 className="h-4 w-4" /></button>
          </div>
        ))}
      </div>
    </div>
  );
}

function LettersTab() {
  const { t } = useI18n();
  const { refreshUser } = useAuth();
  const [letters, setLetters] = useState([]);
  const [form, setForm] = useState({ job_title: "", company: "", job_description: "", context: "" });
  const [loading, setLoading] = useState(false);
  const load = () => api.get("/work/cover-letters").then(({ data }) => setLetters(data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const generate = async () => {
    if (!form.job_title) { toast.error(t("work.jobTitle")); return; }
    setLoading(true);
    try { await api.post("/work/cover-letters", form); toast.success(t("common.success")); setForm({ job_title: "", company: "", job_description: "", context: "" }); load(); refreshUser(); }
    catch (e) { creditError(e, t); } finally { setLoading(false); }
  };
  const del = async (id) => { await api.delete(`/work/cover-letters/${id}`); load(); };
  return (
    <div className="grid lg:grid-cols-2 gap-6">
      <div className="rounded-2xl border bg-card p-5 space-y-3">
        <h3 className="font-display font-semibold">{t("work.newLetter")}</h3>
        <div><Label>{t("work.jobTitle")}</Label><Input value={form.job_title} onChange={(e) => setForm({ ...form, job_title: e.target.value })} data-testid="letter-jobtitle" /></div>
        <div><Label>{t("work.company")}</Label><Input value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} data-testid="letter-company" /></div>
        <div><Label>{t("work.jobDescription")}</Label><Textarea rows={4} value={form.job_description} onChange={(e) => setForm({ ...form, job_description: e.target.value })} data-testid="letter-description" /></div>
        <div><Label>{t("work.context")}</Label><Textarea rows={3} value={form.context} onChange={(e) => setForm({ ...form, context: e.target.value })} data-testid="letter-context" /></div>
        <Button onClick={generate} disabled={loading} className="rounded-full w-full" data-testid="generate-letter-btn"><Sparkles className="h-4 w-4 mr-2" />{loading ? t("common.loading") : t("work.generateLetter")}</Button>
      </div>
      <div className="space-y-3">
        <h3 className="font-display font-semibold">{t("work.myLetters")}</h3>
        {letters.length === 0 && <p className="text-sm text-muted-foreground py-8 text-center">—</p>}
        {letters.map((l) => (
          <div key={l.id} className="rounded-xl border bg-card p-4" data-testid="letter-card">
            <div className="flex items-center justify-between mb-2">
              <div className="font-medium">{l.job_title} {l.company && `· ${l.company}`}</div>
              <div className="flex gap-2">
                <button onClick={() => { navigator.clipboard.writeText(l.content); toast.success(t("common.copied")); }} data-testid="copy-letter"><Copy className="h-4 w-4 text-muted-foreground hover:text-foreground" /></button>
                <button onClick={() => del(l.id)} data-testid="delete-letter"><Trash2 className="h-4 w-4 text-muted-foreground hover:text-destructive" /></button>
              </div>
            </div>
            <p className="text-sm text-muted-foreground whitespace-pre-wrap line-clamp-6">{l.content}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

function TranslateTab() {
  const { t } = useI18n();
  const { refreshUser } = useAuth();
  const [text, setText] = useState("");
  const [src, setSrc] = useState("fr");
  const [tgt, setTgt] = useState("en");
  const [ctx, setCtx] = useState("professional");
  const [result, setResult] = useState("");
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const load = () => api.get("/work/translations").then(({ data }) => setHistory(data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const translate = async () => {
    if (!text.trim()) return;
    setLoading(true);
    try { const { data } = await api.post("/work/translate", { source_text: text, source_lang: src, target_lang: tgt, context: ctx }); setResult(data.translated_text); load(); refreshUser(); }
    catch (e) { creditError(e, t); } finally { setLoading(false); }
  };
  const langs = [["fr", "Français"], ["en", "English"], ["es", "Español"], ["pt", "Português"], ["ar", "العربية"]];
  const ctxs = ["professional", "academic", "administrative", "general", "digital"];
  return (
    <div className="grid lg:grid-cols-2 gap-6">
      <div className="space-y-4">
        <div className="rounded-2xl border bg-card p-5 space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div><Label>{t("work.from")}</Label>
              <Select value={src} onValueChange={setSrc}><SelectTrigger data-testid="translate-src"><SelectValue /></SelectTrigger><SelectContent>{langs.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent></Select>
            </div>
            <div><Label>{t("work.to")}</Label>
              <Select value={tgt} onValueChange={setTgt}><SelectTrigger data-testid="translate-tgt"><SelectValue /></SelectTrigger><SelectContent>{langs.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent></Select>
            </div>
          </div>
          <div><Label>{t("work.translationContext")}</Label>
            <Select value={ctx} onValueChange={setCtx}><SelectTrigger data-testid="translate-ctx"><SelectValue /></SelectTrigger><SelectContent>{ctxs.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent></Select>
          </div>
          <Textarea rows={6} value={text} onChange={(e) => setText(e.target.value)} placeholder={t("work.sourceText")} data-testid="translate-input" />
          <Button onClick={translate} disabled={loading} className="rounded-full w-full" data-testid="translate-btn"><Languages className="h-4 w-4 mr-2" />{loading ? t("common.loading") : t("common.generate")}</Button>
        </div>
        {result && (
          <div className="rounded-2xl border bg-primary/5 p-5" data-testid="translate-result">
            <div className="flex justify-between items-center mb-2"><span className="text-sm font-semibold">{t("work.translation")}</span>
              <button onClick={() => { navigator.clipboard.writeText(result); toast.success(t("common.copied")); }}><Copy className="h-4 w-4 text-muted-foreground" /></button></div>
            <p className="text-sm whitespace-pre-wrap">{result}</p>
          </div>
        )}
      </div>
      <div>
        <h3 className="font-display font-semibold mb-3">{t("work.history")}</h3>
        {history.length === 0 && <p className="text-sm text-muted-foreground">{t("work.noTranslations")}</p>}
        <div className="space-y-2">
          {history.map((h) => (
            <div key={h.id} className="rounded-xl border bg-card p-3 text-sm" data-testid="translation-history-item">
              <div className="text-xs text-muted-foreground mb-1">{h.source_lang} → {h.target_lang} · {h.context}</div>
              <div className="text-muted-foreground line-clamp-1">{h.source_text}</div>
              <div className="line-clamp-2">{h.translated_text}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function JobAnalysisTab() {
  const { t } = useI18n();
  const { refreshUser } = useAuth();
  const [text, setText] = useState("");
  const [result, setResult] = useState("");
  const [loading, setLoading] = useState(false);
  const analyze = async () => {
    if (!text.trim()) return;
    setLoading(true);
    try { const { data } = await api.post("/work/analyze-job", { job_description: text }); setResult(data.analysis); refreshUser(); }
    catch (e) { creditError(e, t); } finally { setLoading(false); }
  };
  return (
    <div className="grid lg:grid-cols-2 gap-6">
      <div className="rounded-2xl border bg-card p-5 space-y-3">
        <Textarea rows={12} value={text} onChange={(e) => setText(e.target.value)} placeholder={t("work.pasteJob")} data-testid="job-input" />
        <Button onClick={analyze} disabled={loading} className="rounded-full w-full" data-testid="analyze-job-btn"><Sparkles className="h-4 w-4 mr-2" />{loading ? t("common.loading") : t("work.analyzeJob")}</Button>
      </div>
      {result && <div className="rounded-2xl border bg-card p-5" data-testid="job-analysis-result"><h3 className="font-display font-semibold mb-2">{t("work.analysisResult")}</h3><p className="text-sm whitespace-pre-wrap">{result}</p></div>}
    </div>
  );
}

export default function Work() {
  const { t } = useI18n();
  return (
    <div className="max-w-6xl mx-auto">
      <PageHeader title={t("work.title")} subtitle={t("work.subtitle")} />
      <Tabs defaultValue="cv">
        <TabsList className="mb-6 flex-wrap h-auto">
          <TabsTrigger value="cv" data-testid="tab-cv"><FileText className="h-4 w-4 mr-1.5" />{t("work.cv")}</TabsTrigger>
          <TabsTrigger value="letters" data-testid="tab-letters"><Briefcase className="h-4 w-4 mr-1.5" />{t("work.letters")}</TabsTrigger>
          <TabsTrigger value="translate" data-testid="tab-translate"><Languages className="h-4 w-4 mr-1.5" />{t("work.translate")}</TabsTrigger>
          <TabsTrigger value="job" data-testid="tab-job"><Sparkles className="h-4 w-4 mr-1.5" />{t("work.jobAnalysis")}</TabsTrigger>
        </TabsList>
        <TabsContent value="cv"><CVTab /></TabsContent>
        <TabsContent value="letters"><LettersTab /></TabsContent>
        <TabsContent value="translate"><TranslateTab /></TabsContent>
        <TabsContent value="job"><JobAnalysisTab /></TabsContent>
      </Tabs>
    </div>
  );
}
